import type { OpeningWindow } from '../../api/client'
import { minutesToTime, timeToMinutes } from '../../catalog/format'

// DraftWindow is one stretch of hours as edited on screen, in "HH:MM".
export interface DraftWindow {
  start: string
  end: string
}

// DraftWeek holds each weekday's stretches; index 0 is Monday.
export type DraftWeek = DraftWindow[][]

export interface WindowPosition {
  day: number
  index: number
}

export function toDraftWeek(windows: OpeningWindow[]): DraftWeek {
  const week: DraftWeek = Array.from({ length: 7 }, () => [])
  for (const w of windows) {
    week[w.weekday]?.push({ start: minutesToTime(w.start_minute), end: minutesToTime(w.end_minute) })
  }
  return week
}

const lastStart = 23 * 60
const lastEnd = 23 * 60 + 55

// nextDraftWindow suggests a stretch starting an hour after the day's last one
// ends, or 09:00–12:00 for an empty day.
export function nextDraftWindow(day: DraftWindow[]): DraftWindow {
  const previousEnd = day.length > 0 ? timeToMinutes(day[day.length - 1].end) : null
  const start = previousEnd === null ? 9 * 60 : Math.min(previousEnd + 60, lastStart)
  const end = Math.min(start + 180, lastEnd)
  return { start: minutesToTime(start), end: minutesToTime(end) }
}

// toWindows flattens the week for the API. positions[i] says where request
// window i sits on screen; errors are keyed "day:index".
export function toWindows(week: DraftWeek): {
  windows: OpeningWindow[]
  positions: WindowPosition[]
  errors: Record<string, string>
} {
  const windows: OpeningWindow[] = []
  const positions: WindowPosition[] = []
  const errors: Record<string, string> = {}
  week.forEach((day, weekday) => {
    day.forEach((draft, index) => {
      const start = timeToMinutes(draft.start)
      const end = timeToMinutes(draft.end)
      if (start === null || end === null) {
        errors[`${weekday}:${index}`] = 'Enter a start and an end time.'
        return
      }
      windows.push({ weekday, start_minute: start, end_minute: end })
      positions.push({ day: weekday, index })
    })
  })
  return { windows, positions, errors }
}

// errorsByWindow maps API fields like "windows[3].end_minute" to "day:index"
// keys; anything not tied to one window goes under "week".
export function errorsByWindow(fields: Record<string, string>, positions: WindowPosition[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [field, message] of Object.entries(fields)) {
    const match = /^windows\[(\d+)\]/.exec(field)
    const position = match ? positions[Number(match[1])] : undefined
    const key = position ? `${position.day}:${position.index}` : 'week'
    if (!(key in out)) {
      out[key] = message
    }
  }
  return out
}

// sameWeek reports whether two drafts hold the same stretches, to spot unsaved edits.
export function sameWeek(a: DraftWeek, b: DraftWeek): boolean {
  return a.length === b.length && a.every((day, d) =>
    day.length === b[d].length && day.every((w, i) => w.start === b[d][i].start && w.end === b[d][i].end))
}

// A day holds at most this many stretches, matching the API's limit.
export const MAX_STRETCHES = 6

export function updateStretch(week: DraftWeek, day: number, index: number, change: Partial<DraftWindow>): DraftWeek {
  return week.map((stretches, d) =>
    d !== day ? stretches : stretches.map((w, i) => (i === index ? { ...w, ...change } : w)),
  )
}

export function addStretch(week: DraftWeek, day: number): DraftWeek {
  return week.map((stretches, d) => (d === day ? [...stretches, nextDraftWindow(stretches)] : stretches))
}

export function removeStretch(week: DraftWeek, day: number, index: number): DraftWeek {
  return week.map((stretches, d) => (d === day ? stretches.filter((_, i) => i !== index) : stretches))
}

const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// weekSummary reads her week the way she'd say it: runs of days with the same
// hours share a line ("Mon–Fri", "09:00–13:00, 14:00–18:00"), and a day with
// none is "Closed".
export function weekSummary(week: DraftWeek): { days: string; hours: string }[] {
  const hoursOf = (day: DraftWindow[]) => (day.length === 0 ? 'Closed' : day.map((w) => `${w.start}–${w.end}`).join(', '))
  const out: { days: string; hours: string }[] = []
  let from = 0
  for (let d = 1; d <= 7; d++) {
    if (d < 7 && hoursOf(week[d] ?? []) === hoursOf(week[from] ?? [])) continue
    const to = d - 1
    const days = from === to ? SHORT_DAYS[from] : to === from + 1 ? `${SHORT_DAYS[from]}, ${SHORT_DAYS[to]}` : `${SHORT_DAYS[from]}–${SHORT_DAYS[to]}`
    out.push({ days, hours: hoursOf(week[from] ?? []) })
    from = d
  }
  return out
}

